import { Channel } from '@/types/channel.types';
import {
  EmailCalendarLinks,
  EmailTextPhrases,
  EmailTypeEnum,
  IScheduledEmailContent,
  IScheduledEmailSettings,
} from '@/types/email.types';
import { BrandliveEvent } from '@/types/projects.types';
import { ColorValueIndexes, EPaletteModes, SafeFonts } from '@/types/theme-packs.types';
import { LanguagesAbbr } from '@/types/translation.types';
import getAdjustedEmailPack from '../helpers/get-adjusted-email-pack';
import { useGetColorOptionsFromColor } from '@/utils/colors/use-get-color-options-from-color';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';
import { JSX } from 'react';
import isRtlLanguage from '@/utils/is-rtl-language';
import { EmailContent, EventEmail } from './event-email';
import { projectNameAndDescription } from '../helpers/project-name-and-description';
import { getProjectStartTime } from '@/utils/get-project-start-time';
import { timeFormatForEmails } from '../helpers/time-format-for-emails';
import { getUrl } from '../helpers/get-url';
import { getDefaultAgendaImage } from '@/utils/modules/get-default-agenda-image';
import { TemplateClassNames } from '@/types/template-layouts.types';
import { sessionNameAndDescription } from '../helpers/session-name-and-description';
import { Session } from '@/types/session.types';
import convertHtmlString from '@/utils/convert-html-string';

/* Note: this is the classic pack id in all environments: dev, qa, staging and prod */
const CLASSIC_PACK_ID = 69;

export interface ScheduledEmailProps {
  emailType: EmailTypeEnum;
  language: LanguagesAbbr;
  workingProject: BrandliveEvent;
  currentChannel: Channel;
  publishedUrl: string;
  emailTranslation: EmailTextPhrases;
  sessionUUID?: string;
  emailSendTime?: number;
  /** if we are fetching the scheduled email to send rather than display
	certain elements will be handled differently */
  emailToSend?: boolean;
  customContent?: IScheduledEmailContent;
  customSettings?: IScheduledEmailSettings;
  calendarLinks?: EmailCalendarLinks;
  onEdit?: (
    value: string | IScheduledEmailContent['session_image'],
    key: string,
    additionalCustomizations?: Record<string, unknown>
  ) => void;
  timezone?: string;
  idx?: string;
  sent?: boolean;
  hideCalendar?: boolean;
  icsFileContent?: { icsFileText: string; fileName: string } | null;
}

// email body to display in editor
export const ScheduledEmail = ({
  emailType,
  language,
  workingProject,
  currentChannel,
  publishedUrl,
  emailTranslation,
  sessionUUID,
  emailSendTime,
  emailToSend = false,
  customContent,
  customSettings,
  calendarLinks,
  onEdit,
  timezone = 'America/Los_Angeles',
  idx,
  sent,
  hideCalendar,
  icsFileContent,
}: ScheduledEmailProps): JSX.Element => {
  const session = sessionUUID ? workingProject?.sessions.find((session) => session.uuid === sessionUUID) : undefined;

  const template = workingProject.template.template;
  let emailPack = workingProject.settings.email?.colors;
  const eventPack = workingProject.settings.design?.colors;
  const eventMode = workingProject.settings.design?.color_theme;

  let paletteMode = workingProject.settings.email?.color_palette_mode ?? EPaletteModes.Light;

  const adjustedEvent = getAdjustedEmailPack(workingProject, eventMode || paletteMode, eventPack || emailPack);
  emailPack = adjustedEvent.settings.email?.colors;
  paletteMode = adjustedEvent.settings.email?.color_palette_mode ?? paletteMode;

  const emailColorOptions = useGetColorOptionsFromColor(emailPack, paletteMode);
  const eventColorOptions = useGetColorOptionsFromColor(eventPack, paletteMode);

  let isAutoGenPack = false;

  let isDefaultPack = false;
  if (emailPack) {
    if (emailPack?.channel === 0) {
      isDefaultPack = true;
    }
  } else if (eventPack) {
    if (eventPack?.channel === 0) {
      isDefaultPack = true;
    }
  }

  if (emailPack) {
    isAutoGenPack = emailPack?.creation_type === 'auto';
  } else {
    isAutoGenPack = eventPack?.creation_type === 'auto';
  }

  let colors = emailColorOptions ?? eventColorOptions ?? {};
  let anchorColor: string | undefined;
  // because our email palettes are simplified and we only have text colors on container colors
  // we can simply override the text colors with their relative contrast on the container color
  if (isAutoGenPack) {
    const fallback = paletteMode === EPaletteModes.Light ? '#ffffff' : '#000000';
    const headingTextColor = getRelativeContrastColor(
      colors?.headingTextColor?.[ColorValueIndexes.hex] || fallback,
      colors?.containerColor?.[ColorValueIndexes.hex] || fallback
    );
    const bodyTextColor = getRelativeContrastColor(
      colors?.bodyTextColor?.[ColorValueIndexes.hex] || fallback,
      colors?.containerColor?.[ColorValueIndexes.hex] || fallback
    );
    const secondaryAccentColor = getRelativeContrastColor(
      colors?.secondaryAccentColor?.[ColorValueIndexes.hex] || fallback,
      colors?.containerColor?.[ColorValueIndexes.hex] || fallback
    );

    const _anchorColor = getRelativeContrastColor(
      '#0000EE',
      colors?.containerColor?.[ColorValueIndexes.hex] || fallback
    );
    if (_anchorColor !== '#0000EE') {
      // keep it undefined if it is the default
      anchorColor = _anchorColor;
    }

    colors = {
      ...colors,
      headingTextColor: [headingTextColor, 1],
      bodyTextColor: [bodyTextColor, 1],
      secondaryAccentColor: [secondaryAccentColor, 1],
    };
  }

  // technically speaking we don't need this isDefaultPack wrapper validation since we are explicitly checking for classic pack
  // but keeping it just in case we need to do other default pack overrides at some point
  if (isDefaultPack) {
    // because classic pack container color is white and so is heading text, we need to override it
    // to body text color because that's a valid contrast
    const packId = emailPack?.color_pack ?? eventPack?.color_pack;
    if (packId === CLASSIC_PACK_ID) {
      colors = {
        ...colors,
        headingTextColor: colors.bodyTextColor,
      };
    }
  }

  const hideBrandliveLogo = workingProject.settings.email?.hideBrandliveLogo ?? false;
  const safeFont = workingProject.settings.email?.font ?? SafeFonts.Arial;
  const clientLogo: string =
    currentChannel.logos.color ?? currentChannel.logos.white ?? currentChannel.logos.gray ?? '';

  const content =
    emailType.includes('session') && session
      ? getSessionEmailContent(
          emailType as EmailTypeEnum,
          language,
          workingProject,
          currentChannel,
          publishedUrl,
          session,
          emailSendTime,
          timezone,
          icsFileContent || undefined
        )
      : getEventEmailContent(
          emailType as EmailTypeEnum,
          language,
          workingProject,
          currentChannel,
          publishedUrl,
          timezone,
          icsFileContent || undefined
        );
  const emailContent = { ...content, translations: emailTranslation, settings: customSettings, calendarLinks };

  const forceRtl = isRtlLanguage(language);

  if (sessionUUID && !session) return <></>;
  if (!colors) return <></>;
  return EventEmail({
    emailType: emailType as EmailTypeEnum,
    template,
    colors,
    fonts: safeFont,
    hideBrandliveLogo,
    clientLogo,
    emailContent,
    language,
    emailToSend,
    customContent,
    onEdit,
    idx,
    sent,
    forceRtl,
    hideCalendar,
    anchorColor,
  });
};

const getEventEmailContent = (
  emailType: EmailTypeEnum,
  language: LanguagesAbbr,
  workingProject: BrandliveEvent,
  currentChannel: Channel,
  publishedUrl: string,
  timezone = 'America/Los_Angeles',
  icsFileContent?: { icsFileText: string; fileName: string }
): Omit<EmailContent, 'translations'> => {
  const { name, image: bannerImage } = projectNameAndDescription(workingProject, language);

  const eventStart = getProjectStartTime({ project: workingProject });
  const start: string | null = eventStart ? timeFormatForEmails(eventStart, language, timezone) : null;

  const registrationRequired = workingProject.registration_on;

  const url = getUrl(currentChannel, workingProject, publishedUrl, language);

  const image = bannerImage ?? getDefaultAgendaImage(TemplateClassNames[workingProject.template.name], 0);

  return {
    event: {
      name: convertHtmlString(name),
      description: convertHtmlString(workingProject.registration_settings?.description?.base || ''),
      start,
      registrationRequired,
      url,
      image,
      icsFileContent,
    },
  };
};

const getSessionEmailContent = (
  emailType: EmailTypeEnum,
  language: LanguagesAbbr,
  workingProject: BrandliveEvent,
  currentChannel: Channel,
  publishedUrl: string,
  session: Session,
  emailSendTime: number | undefined,
  timezone = 'America/Los_Angeles',
  icsFileContent?: { icsFileText: string; fileName: string }
): Omit<EmailContent, 'translations'> => {
  const { name, description } = sessionNameAndDescription(session, language);

  const url = getUrl(currentChannel, workingProject, publishedUrl, language, session.uuid);

  // We want the findIndex because it'll keep the default image consistent across renders. Once the image (default or uploaded) is saved, the default function will stop executing.
  const image =
    session.image ??
    session.pre_broadcast_thumbnail ??
    getDefaultAgendaImage(
      TemplateClassNames[workingProject.template.name],
      workingProject.sessions.findIndex((_session) => _session.uuid === session.uuid)
    );

  const start: string | null = session.timestamp ? timeFormatForEmails(session.timestamp, language, timezone) : null;
  const oneDay = 24 * 60 * 60 * 1000;
  const daysToStart =
    emailType === EmailTypeEnum.session_reminder && session.timestamp && emailSendTime
      ? Math.floor(session.timestamp.valueOf() / oneDay) - Math.floor(emailSendTime.valueOf() / oneDay)
      : undefined;

  return {
    session: {
      name,
      description: description,
      url,
      image,
      start,
      daysToStart,
      icsFileContent,
    },
  };
};
